/// <reference lib="dom" />

import type { LeetIntensity, PasswordStrength } from "./common";
import { applyStaticTranslations, getCurrentTranslation, getNextLanguage, LANGUAGE_STORAGE_KEY, resolveInitialLanguage } from "./i18n";
import type { UiLanguageCode } from "./i18n";

type PasswordUnitType = "symbol" | "word" | "digits";

type PasswordUnit = {
	type: PasswordUnitType;
	length: number;
};

type PasswordResponse = {
	length: number;
	leetIntensity: LeetIntensity;
	useUmlauts: boolean;
	useUnicodeSymbols: boolean;
	password: string;
	units: PasswordUnit[];
	strength: PasswordStrength;
	error?: string;
};

type WebConfigResponse = {
	minimumLength: number;
	maximumLength: number;
	defaultLength: number;
};

type WebConfig = {
	minimumLength: number;
	maximumLength: number;
	defaultLength: number;
};

const lengthSlider = document.querySelector<HTMLInputElement>("#password-length");
const lengthValueInput = document.querySelector<HTMLInputElement>("#length-value");
const generateButton = document.querySelector<HTMLButtonElement>("#generate-button");
const copyButton = document.querySelector<HTMLButtonElement>("#copy-button");
const languageButton = document.querySelector<HTMLButtonElement>("#language-button");
const leetIntensitySelect = document.querySelector<HTMLSelectElement>("#leet-intensity");
const umlautCheckbox = document.querySelector<HTMLInputElement>("#use-umlauts");
const unicodeSymbolsCheckbox = document.querySelector<HTMLInputElement>("#use-unicode-symbols");
const passwordOutput = document.querySelector<HTMLParagraphElement>("#password-output");
const strengthLabel = document.querySelector<HTMLParagraphElement>("#strength-label");
const strengthScore = document.querySelector<HTMLParagraphElement>("#strength-score");
const strengthFill = document.querySelector<HTMLDivElement>("#strength-fill");
const entropyValue = document.querySelector<HTMLSpanElement>("#entropy-value");
const crackTimeValue = document.querySelector<HTMLSpanElement>("#crack-time-value");
const strengthMeter = document.querySelector<HTMLDivElement>(".strength-meter");
const statusMessage = document.querySelector<HTMLParagraphElement>("#status-message");
export const translatableElements = document.querySelectorAll<HTMLElement>("[data-i18n]");

function getRequiredElement<T extends Element>(
	element: T | null,
	name: string,
): T {
	if (element === null) {
		throw new Error(`Web application could not initialize required DOM element: ${name}`);
	}

	return element;
}

const readyLengthSlider = getRequiredElement(lengthSlider, "#password-length");
const readyLengthValueInput = getRequiredElement(lengthValueInput, "#length-value");
const readyGenerateButton = getRequiredElement(generateButton, "#generate-button");
const readyCopyButton = getRequiredElement(copyButton, "#copy-button");
const readyLanguageButton = getRequiredElement(languageButton, "#language-button");
const readyLeetIntensitySelect = getRequiredElement(leetIntensitySelect, "#leet-intensity");
const readyUmlautCheckbox = getRequiredElement(umlautCheckbox, "#use-umlauts");
const readyUnicodeSymbolsCheckbox = getRequiredElement(
	unicodeSymbolsCheckbox,
	"#use-unicode-symbols",
);
const readyPasswordOutput = getRequiredElement(passwordOutput, "#password-output");
const readyStrengthLabel = getRequiredElement(strengthLabel, "#strength-label");
const readyStrengthScore = getRequiredElement(strengthScore, "#strength-score");
const readyStrengthFill = getRequiredElement(strengthFill, "#strength-fill");
const readyEntropyValue = getRequiredElement(entropyValue, "#entropy-value");
const readyCrackTimeValue = getRequiredElement(crackTimeValue, "#crack-time-value");
const readyStrengthMeter = getRequiredElement(strengthMeter, ".strength-meter");
const readyStatusMessage = getRequiredElement(statusMessage, "#status-message");

export let currentLanguage: UiLanguageCode = "de";
let webConfig: WebConfig = {
	minimumLength: 6,
	maximumLength: 128,
	defaultLength: 20,
};

let lastCommittedLength = webConfig.defaultLength;
let hasGeneratedPassword = false;
let lastStrength: PasswordStrength | null = null;

function applyLanguage(language: UiLanguageCode): void {
	currentLanguage = language;
	const translation = getCurrentTranslation(currentLanguage);

	document.documentElement.lang = language;
	document.title = translation.documentTitle;
	applyStaticTranslations(currentLanguage, translatableElements);

	const nextLanguage = getNextLanguage(language);
	readyLanguageButton.textContent = translation.nextLanguageButtonLabel(nextLanguage);

	if (!hasGeneratedPassword) {
		readyPasswordOutput.textContent = translation.passwordPlaceholder;
	}

	if (lastStrength === null) {
		readyStrengthLabel.textContent = translation.staticTexts.strengthPending;
		readyStrengthScore.textContent = translation.strengthScoreText(0);
		readyCrackTimeValue.textContent = translation.crackTimeUnknown;
	} else {
		renderStrength(lastStrength);
	}

	try {
		localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
	} catch {
		// Ignore storage access errors.
	}
}

function renderStrength(strength: PasswordStrength): void {
	lastStrength = strength;
	const translation = getCurrentTranslation(currentLanguage);
	const scorePercent = ((strength.score + 1) / 5) * 100;
	readyStrengthFill.style.width = `${scorePercent}%`;
	readyStrengthFill.dataset.score = strength.score.toString();
	readyStrengthLabel.textContent = translation.strengthLabelsByScore[strength.score];
	readyStrengthScore.textContent = translation.strengthScoreText(strength.score);
	readyEntropyValue.textContent = strength.entropyBits.toFixed(1);
	readyCrackTimeValue.textContent = strength.estimatedCrackTime;
	readyStrengthMeter.setAttribute("aria-valuenow", strength.score.toString());
}

function setStatus(message: string, isError: boolean): void {
	readyStatusMessage.textContent = message;
	readyStatusMessage.dataset.state = isError ? "error" : "ok";
}

async function generatePassword(): Promise<void> {
	const translation = getCurrentTranslation(currentLanguage);
	const length = lastCommittedLength;
	const leetIntensity = readyLeetIntensitySelect.value as LeetIntensity;
	const useUmlauts = readyUmlautCheckbox.checked;
	const useUnicodeSymbols = readyUnicodeSymbolsCheckbox.checked;
	setStatus(translation.statusGenerating, false);

	try {
		const response = await fetch(
			`/api/password?length=${length}&leetIntensity=${leetIntensity}&umlauts=${useUmlauts ? "1" : "0"}&unicodeSymbols=${useUnicodeSymbols ? "1" : "0"}`,
		);
		const data = (await response.json()) as PasswordResponse;

		if (!response.ok || data.error !== undefined) {
			throw new Error(data.error ?? translation.statusUnableToGenerate);
		}

		hasGeneratedPassword = true;
		readyPasswordOutput.textContent = data.password;
		renderStrength(data.strength);
		setStatus(
			translation.statusGenerated({
				length: data.length,
				leetIntensity: data.leetIntensity,
				useUmlauts: data.useUmlauts,
				useUnicodeSymbols: data.useUnicodeSymbols,
			}),
			false,
		);
	} catch (error) {
		const message = error instanceof Error ? error.message : translation.statusUnknownError;
		setStatus(message, true);
	}
}

function clampLength(value: number): number {
	if (!Number.isInteger(value)) {
		return webConfig.defaultLength;
	}

	if (value < webConfig.minimumLength) {
		return webConfig.minimumLength;
	}

	if (value > webConfig.maximumLength) {
		return webConfig.maximumLength;
	}

	return value;
}

function syncLengthControls(nextLength: number): void {
	const clampedLength = clampLength(nextLength);
	readyLengthSlider.value = clampedLength.toString();
	readyLengthValueInput.value = clampedLength.toString();
	lastCommittedLength = clampedLength;
}

function commitLengthInput(): boolean {
	const translation = getCurrentTranslation(currentLanguage);
	const parsedLength = Number.parseInt(readyLengthValueInput.value, 10);
	if (!Number.isInteger(parsedLength)) {
		setStatus(translation.statusValidInteger, true);
		syncLengthControls(lastCommittedLength);
		return false;
	}

	syncLengthControls(parsedLength);
	return true;
}

function applyWebConfig(config: WebConfig): void {
	webConfig = config;
	readyLengthSlider.min = config.minimumLength.toString();
	readyLengthSlider.max = config.maximumLength.toString();
	readyLengthValueInput.min = config.minimumLength.toString();
	readyLengthValueInput.max = config.maximumLength.toString();
	syncLengthControls(config.defaultLength);
}

async function loadWebConfig(): Promise<void> {
	try {
		const response = await fetch("/api/config");
		const data = (await response.json()) as WebConfigResponse;

		if (
			!response.ok ||
			!Number.isInteger(data.minimumLength) ||
			!Number.isInteger(data.maximumLength) ||
			!Number.isInteger(data.defaultLength)
		) {
			throw new Error("Invalid web config response");
		}

		applyWebConfig(data);
	} catch {
		applyWebConfig(webConfig);
	}
}

readyLengthSlider.addEventListener("input", () => {
	syncLengthControls(Number.parseInt(readyLengthSlider.value, 10));
});

readyLengthSlider.addEventListener("wheel", (evt) => {
	const deltaY = evt.deltaY;
	if (deltaY > 0)
		syncLengthControls(Number.parseInt(readyLengthSlider.value, 10) + 1);
	else if (deltaY < 0)
		syncLengthControls(Number.parseInt(readyLengthSlider.value, 10) - 1);

	evt.preventDefault();
});

readyLengthValueInput.addEventListener("blur", () => {
	commitLengthInput();
});

readyLengthValueInput.addEventListener("keydown", (event: KeyboardEvent) => {
	if (event.key !== "Enter") {
		return;
	}

	event.preventDefault();
	if (commitLengthInput()) {
		void generatePassword();
	}
});

readyGenerateButton.addEventListener("click", () => {
	if (commitLengthInput()) {
		void generatePassword();
	}
});

readyLanguageButton.addEventListener("click", () => {
	const nextLanguage = getNextLanguage(currentLanguage);
	applyLanguage(nextLanguage);
});

readyCopyButton.addEventListener("click", async () => {
	const translation = getCurrentTranslation(currentLanguage);
	const password = readyPasswordOutput.textContent ?? "";
	if (!hasGeneratedPassword || password.trim().length === 0) {
		setStatus(translation.statusGenerateFirst, true);
		return;
	}

	try {
		await navigator.clipboard.writeText(password);
		setStatus(translation.statusCopied, false);
	} catch {
		setStatus(translation.statusCopyFailed, true);
	}
});

applyLanguage(resolveInitialLanguage());
await loadWebConfig();
await generatePassword();